import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { 
  UsersIcon, 
  BuildingOfficeIcon, 
  ChartBarIcon, 
  CogIcon,
  EyeIcon,
  TrashIcon,
  UserPlusIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import { toast } from 'react-hot-toast'

const SuperAdminDashboard = ({ currentUser, onLogout }) => {
  const [tenants, setTenants] = useState([])
  const [users, setUsers] = useState([])
  const [quotes, setQuotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalTenants: 0,
    totalUsers: 0,
    totalQuotes: 0,
    monthlyRevenue: 0
  })

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      
      // Load tenants (users with role 'user' or 'admin')
      const usersResponse = await fetch('/api/admin/users', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      })
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json()
        setUsers(usersData)
        
        // Group users by tenant_id to get tenant count
        const tenantGroups = usersData.reduce((acc, user) => {
          if (!acc[user.tenant_id]) {
            acc[user.tenant_id] = {
              tenant_id: user.tenant_id,
              company_name: user.company_name,
              owner: user,
              users: []
            }
          }
          acc[user.tenant_id].users.push(user)
          return acc
        }, {})
        
        setTenants(Object.values(tenantGroups))
        
        setStats({
          totalTenants: Object.keys(tenantGroups).length,
          totalUsers: usersData.length,
          totalQuotes: 0, // Will be loaded separately
          monthlyRevenue: 0 // Placeholder
        })
      }
      
    } catch (error) {
      console.error('Error loading dashboard data:', error)
      toast.error('Fehler beim Laden der Dashboard-Daten')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Benutzer wirklich löschen? Dies kann nicht rückgängig gemacht werden.')) {
      return
    }

    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      })

      if (response.ok) {
        toast.success('Benutzer erfolgreich gelöscht')
        loadDashboardData()
      } else {
        throw new Error('Löschen fehlgeschlagen')
      }
    } catch (error) {
      toast.error('Fehler beim Löschen des Benutzers')
    }
  }

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-100 text-red-800 border-red-200'
      case 'admin': return 'bg-blue-100 text-blue-800 border-blue-200'
      case 'user': return 'bg-green-100 text-green-800 border-green-200'
      default: return 'bg-gray-100 text-gray-800 border-gray-200'
    }
  }

  if (currentUser?.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <ShieldCheckIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-800 mb-2">Zugriff verweigert</h2>
            <p className="text-red-600 mb-4">Sie haben keine Berechtigung für den Super-Admin Bereich.</p>
            <Button onClick={onLogout} variant="outline">Zurück zum Login</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-red-600 rounded-lg flex items-center justify-center">
                <ShieldCheckIcon className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Super-Admin Dashboard</h1>
                <p className="text-xs text-slate-600">Plattform-Verwaltung</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="text-sm font-medium text-slate-800">{currentUser.full_name}</div>
                <div className="text-xs text-red-600">Super Administrator</div>
              </div>
              <Button variant="outline" size="sm" onClick={onLogout}>
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6 text-center">
              <BuildingOfficeIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-slate-800">{stats.totalTenants}</div>
              <div className="text-sm text-slate-600">Aktive Tenants</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6 text-center">
              <UsersIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-slate-800">{stats.totalUsers}</div>
              <div className="text-sm text-slate-600">Registrierte Benutzer</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6 text-center">
              <ChartBarIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-slate-800">{stats.totalQuotes}</div>
              <div className="text-sm text-slate-600">Erstellte Angebote</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6 text-center">
              <CogIcon className="h-8 w-8 text-orange-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-slate-800">100%</div>
              <div className="text-sm text-slate-600">System-Health</div>
            </CardContent>
          </Card>
        </div>

        {/* Tenants Management */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <BuildingOfficeIcon className="h-5 w-5 mr-2" />
                Tenant-Verwaltung
              </CardTitle>
              <Button className="bg-blue-600 hover:bg-blue-700">
                <UserPlusIcon className="h-4 w-4 mr-2" />
                Neuer Tenant
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-slate-600 mt-2">Lade Tenant-Daten...</p>
              </div>
            ) : (
              <div className="space-y-4">
                {tenants.map((tenant) => (
                  <div key={tenant.tenant_id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div>
                            <h3 className="font-semibold text-slate-800">{tenant.company_name}</h3>
                            <div className="text-sm text-slate-600">
                              Owner: {tenant.owner.full_name} ({tenant.owner.email})
                            </div>
                            <div className="text-xs text-slate-500">
                              Tenant ID: {tenant.tenant_id} • {tenant.users.length} Benutzer
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Badge className={getRoleColor(tenant.owner.role)}>
                          {tenant.owner.role}
                        </Badge>
                        
                        <Badge 
                          className={
                            tenant.owner.is_active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }
                        >
                          {tenant.owner.is_active ? 'Aktiv' : 'Inaktiv'}
                        </Badge>
                        
                        <div className="flex space-x-1">
                          <Button size="sm" variant="ghost">
                            <EyeIcon className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleDeleteUser(tenant.owner.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Tenant Users */}
                    {tenant.users.length > 1 && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <div className="text-xs text-slate-600 mb-2">Team-Mitglieder:</div>
                        <div className="flex flex-wrap gap-2">
                          {tenant.users.filter(u => u.id !== tenant.owner.id).map((user) => (
                            <Badge key={user.id} variant="outline" className="text-xs">
                              {user.full_name} ({user.role})
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* System Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                  <div>
                    <div className="font-medium text-blue-800">Neuer Benutzer registriert</div>
                    <div className="text-xs text-blue-600">vor 2 Minuten</div>
                  </div>
                  <Badge className="bg-blue-100 text-blue-800">User</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <div>
                    <div className="font-medium text-green-800">Angebot erstellt</div>
                    <div className="text-xs text-green-600">vor 5 Minuten</div>
                  </div>
                  <Badge className="bg-green-100 text-green-800">Quote</Badge>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                  <div>
                    <div className="font-medium text-purple-800">PDF generiert</div>
                    <div className="text-xs text-purple-600">vor 8 Minuten</div>
                  </div>
                  <Badge className="bg-purple-100 text-purple-800">Export</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>System Configuration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Max. Angebote pro Tenant
                  </label>
                  <Input type="number" defaultValue="1000" className="w-full" />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    PDF Storage Limit (MB)
                  </label>
                  <Input type="number" defaultValue="500" className="w-full" />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Token Expiry (Stunden)
                  </label>
                  <Input type="number" defaultValue="24" className="w-full" />
                </div>
                
                <Button className="w-full bg-orange-600 hover:bg-orange-700">
                  Konfiguration speichern
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default SuperAdminDashboard